/* =====================================================================
 * Widget font bridge.
 *
 * The widget iframe runs `sandbox="allow-scripts"` (so, an opaque origin)
 * under its own `font-src 'none'` CSP, which means it cannot fetch a
 * webfont by URL — not even from this same origin. Handing it the raw
 * font bytes over postMessage sidesteps the fetch entirely: building a
 * `FontFace` from an ArrayBuffer issues no request, so `font-src` never
 * applies and the sandbox stays exactly as tight as it was.
 *
 * Rather than shipping a second copy of Pretendard for the widget, we
 * reuse the dynamic-subset faces the app itself already loaded. Their
 * URLs and `unicode-range`s are read straight out of the CSSOM, and only
 * the subsets covering characters that actually appear in the widget
 * markup get fetched — typically 2-4 files instead of all 92.
 *
 * Every step is best-effort: if the CSSOM is unreadable or a fetch fails
 * the widget simply keeps rendering in system fonts, exactly as it did
 * before this bridge existed.
 * ===================================================================== */

/** Font family the app's `body` stack leads with (see design-system tokens). */
const FONT_FAMILY = 'Pretendard Variable';

/** Variable-weight axis span declared by Pretendard's `@font-face` rules. */
export const WIDGET_FONT_WEIGHT = '45 920';

export interface WidgetFontFace {
  /** Raw woff2 bytes — structured-cloned into the sandbox. */
  readonly buffer: ArrayBuffer;
  /** Verbatim `unicode-range` descriptor, so the sandbox rebuilds the same subset. */
  readonly unicodeRange: string;
}

interface SubsetRule {
  readonly url: string;
  readonly unicodeRange: string;
  readonly ranges: ReadonlyArray<readonly [number, number]>;
}

/**
 * Parse a CSS `unicode-range` descriptor into inclusive code-point pairs.
 *
 * Handles the two forms Pretendard emits — `U+ac00-d7a3` and a bare
 * `U+ff07` — and ignores anything malformed. Wildcard ranges (`U+27??`)
 * are not produced by the subset CSS and are skipped rather than guessed.
 */
export function parseUnicodeRanges(
  value: string,
): ReadonlyArray<readonly [number, number]> {
  const ranges: Array<readonly [number, number]> = [];
  for (const token of value.split(',')) {
    const match = /^u\+([0-9a-f]+)(?:-([0-9a-f]+))?$/i.exec(token.trim());
    if (!match) continue;
    const start = Number.parseInt(match[1], 16);
    const end = match[2] === undefined ? start : Number.parseInt(match[2], 16);
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) continue;
    ranges.push([start, end]);
  }
  return ranges;
}

/** True when any code point in `text` falls inside one of `ranges`. */
export function rangesCoverText(
  ranges: ReadonlyArray<readonly [number, number]>,
  text: string,
): boolean {
  for (const char of text) {
    const code = char.codePointAt(0);
    if (code === undefined) continue;
    for (const [start, end] of ranges) {
      if (code >= start && code <= end) return true;
    }
  }
  return false;
}

/** `src: url(...) format(...)` → the first URL, unquoted. */
function firstSrcUrl(src: string): string | null {
  const match = /url\(\s*(['"]?)([^'")]+)\1\s*\)/.exec(src);
  return match ? match[2].trim() : null;
}

let cachedSubsets: SubsetRule[] | null = null;

/**
 * Collect the app's own Pretendard subset `@font-face` rules from the
 * CSSOM. Cross-origin stylesheets throw on `.cssRules` access, so each
 * sheet is probed defensively.
 */
function readSubsetRules(): SubsetRule[] {
  if (cachedSubsets) return cachedSubsets;
  const subsets: SubsetRule[] = [];
  if (typeof document !== 'undefined') {
    for (const sheet of Array.from(document.styleSheets)) {
      let rules: CSSRuleList;
      try {
        rules = sheet.cssRules;
      } catch {
        continue;
      }
      for (const rule of Array.from(rules)) {
        const style = (rule as CSSFontFaceRule).style as CSSStyleDeclaration | undefined;
        if (!style || !style.getPropertyValue) continue;
        if (!style.getPropertyValue('font-family').includes(FONT_FAMILY)) continue;
        const unicodeRange = style.getPropertyValue('unicode-range').trim();
        const url = firstSrcUrl(style.getPropertyValue('src'));
        if (!unicodeRange || !url) continue;
        subsets.push({ url, unicodeRange, ranges: parseUnicodeRanges(unicodeRange) });
      }
    }
  }
  cachedSubsets = subsets;
  return subsets;
}

const bufferCache = new Map<string, Promise<ArrayBuffer | null>>();

function loadBuffer(url: string): Promise<ArrayBuffer | null> {
  const cached = bufferCache.get(url);
  if (cached) return cached;
  const pending = fetch(url)
    .then((response) => (response.ok ? response.arrayBuffer() : null))
    .catch(() => null);
  bufferCache.set(url, pending);
  return pending;
}

/**
 * Resolve the font subsets needed to render `text` inside the sandbox.
 *
 * Returns an empty array when there is nothing to send — no Pretendard in
 * the CSSOM (unit tests, a tenant that disabled it) or no subset covering
 * the widget's characters.
 */
export async function collectWidgetFontFaces(text: string): Promise<WidgetFontFace[]> {
  const needed = readSubsetRules().filter((subset) =>
    rangesCoverText(subset.ranges, text),
  );
  if (needed.length === 0) return [];

  const loaded = await Promise.all(
    needed.map(async (subset) => {
      const buffer = await loadBuffer(subset.url);
      return buffer ? { buffer, unicodeRange: subset.unicodeRange } : null;
    }),
  );
  return loaded.filter((face): face is WidgetFontFace => face !== null);
}
